"use client";

import { useEffect } from "react";
import Link from "next/link";
import "../landing.css";

/* ──────────────────────────────────────────────────────────
   Combined Terms of Use + Privacy Policy
   ──────────────────────────────────────────────────────────
   Single-page legal document for PlotScore. Tailored to a
   Dutch-based SaaS handling personal data and aggregating
   public property registries. Plain English, GDPR-compliant.

   Items in [SQUARE BRACKETS] need to be filled in by the
   operator before going to market and should be reviewed
   by qualified Dutch / EU counsel.
────────────────────────────────────────────────────────── */

const LAST_UPDATED = "April 7, 2026";

export default function LegalPage() {
  useEffect(() => {
    document.body.classList.add("landing-body");
    return () => {
      document.body.classList.remove("landing-body");
    };
  }, []);

  return (
    <div className="font-brand antialiased">
      {/* ── Minimal nav ── */}
      <nav className="border-b border-brand-border">
        <div className="max-w-[1120px] mx-auto px-5 sm:px-8 flex items-center justify-between h-14 sm:h-16">
          <Link
            href="/"
            className="font-display text-[1.35rem] sm:text-[1.5rem] font-semibold text-brand-linen tracking-[0.02em]"
          >
            PlotScore
          </Link>
          <Link
            href="/"
            className="text-sm text-brand-sage hover:text-brand-linen transition-colors"
          >
            ← Back to home
          </Link>
        </div>
      </nav>

      <main className="py-16 sm:py-24">
        <article className="max-w-[720px] mx-auto px-5 sm:px-8">
          {/* ── Header ── */}
          <header className="mb-14">
            <p className="text-[0.7rem] text-brand-meadow uppercase tracking-[0.25em] mb-5">
              Legal
            </p>
            <h1 className="font-display text-[2.25rem] sm:text-[3rem] font-semibold text-brand-linen leading-[1.15] tracking-[0.01em] mb-6">
              Terms &amp; Privacy
            </h1>
            <p className="text-[0.85rem] text-brand-muted font-data">
              Last updated: {LAST_UPDATED}
            </p>
          </header>

          {/* ── TL;DR ── */}
          <section className="mb-14 p-6 sm:p-7 bg-brand-pine border border-brand-border rounded-2xl">
            <p className="text-[0.65rem] text-brand-meadow uppercase tracking-[0.18em] mb-3">
              The short version
            </p>
            <ul className="space-y-2.5 text-[0.95rem] text-brand-sage leading-[1.7]">
              <li className="flex gap-3">
                <span className="text-brand-meadow flex-shrink-0">·</span>
                <span>
                  PlotScore aggregates public Dutch property data and turns
                  it into scores. We do not own or guarantee that data.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="text-brand-meadow flex-shrink-0">·</span>
                <span>
                  Our scores are estimates, not professional appraisals or
                  financial advice. Always verify before acting.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="text-brand-meadow flex-shrink-0">·</span>
                <span>
                  We collect your email when you create an account. We
                  don&apos;t sell it, we don&apos;t share it with advertisers,
                  and you can ask us to delete it any time.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="text-brand-meadow flex-shrink-0">·</span>
                <span>
                  No third-party trackers. No advertising cookies. Just
                  what we need to run the service.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="text-brand-meadow flex-shrink-0">·</span>
                <span>
                  GDPR applies. You have real rights over your data and we
                  honor them.
                </span>
              </li>
            </ul>
          </section>

          {/* ── Table of contents ── */}
          <nav className="mb-14 border-y border-brand-border py-5">
            <p className="text-[0.65rem] text-brand-muted uppercase tracking-[0.18em] mb-3">
              Contents
            </p>
            <ol className="space-y-1.5 text-[0.9rem] text-brand-sage font-data">
              <li>
                <a href="#terms" className="hover:text-brand-meadow transition-colors">
                  01 &nbsp;&nbsp;Terms of Use
                </a>
              </li>
              <li>
                <a href="#privacy" className="hover:text-brand-meadow transition-colors">
                  02 &nbsp;&nbsp;Privacy Policy
                </a>
              </li>
              <li>
                <a href="#contact" className="hover:text-brand-meadow transition-colors">
                  03 &nbsp;&nbsp;Contact &amp; Complaints
                </a>
              </li>
            </ol>
          </nav>

          {/* ═════════════════════════════════════════════
             TERMS OF USE
             ═════════════════════════════════════════════ */}
          <section id="terms" className="mb-20 scroll-mt-24">
            <p className="text-[0.65rem] text-brand-meadow uppercase tracking-[0.25em] mb-3">
              Section 01
            </p>
            <h2 className="font-display text-[1.85rem] sm:text-[2.25rem] font-semibold text-brand-linen leading-[1.15] tracking-[0.01em] mb-10">
              Terms of Use
            </h2>

            <LegalSection num="1.1" title="Who we are">
              <p>
                PlotScore is operated by{" "}
                <span className="text-brand-linen">[Company Name B.V.]</span>,
                a company registered in the Netherlands with its registered
                office in Amsterdam{" "}
                <span className="text-brand-muted">
                  (KvK [number], BTW [VAT number])
                </span>
                . When we say &ldquo;PlotScore,&rdquo; &ldquo;we,&rdquo;
                &ldquo;us,&rdquo; or &ldquo;our,&rdquo; we mean that
                company. When we say &ldquo;you&rdquo; or
                &ldquo;your,&rdquo; we mean you, the person using PlotScore.
              </p>
            </LegalSection>

            <LegalSection num="1.2" title="What this is">
              <p>
                PlotScore is an information and analytics service that
                aggregates publicly available data about properties in the
                Netherlands and presents that data in the form of scores
                and reports. The service is live, with a free tier and paid subscription plans.
              </p>
              <p>
                By using PlotScore &mdash; including creating an account, accessing the application, or browsing
                plotscore.app &mdash; you agree to these Terms. If you do
                not agree, please do not use the service.
              </p>
            </LegalSection>

            <LegalSection num="1.3" title="Eligibility">
              <p>
                You must be at least 18 years old and capable of entering
                into a binding agreement to use PlotScore. If you are using
                PlotScore on behalf of a company or organization, you
                confirm that you have the authority to bind that entity to
                these Terms.
              </p>
            </LegalSection>

            <LegalSection num="1.4" title="Accounts and access">
              <p>
                You can create a free account to use PlotScore and upgrade to a paid plan (Buyer's Pass, Pro, or Studio) at any time. We may grant, deny, or revoke access at our discretion, including for violation of these Terms.
              </p>
              <p>
                We may add, remove, or change features at any time. We aim for high availability but do not guarantee uninterrupted service; the service may experience downtime, bugs, or data inaccuracies.
              </p>
            </LegalSection>

            <LegalSection num="1.5" title="Data accuracy &mdash; please read carefully">
              <p>
                PlotScore aggregates data from public sources including
                (but not limited to) Kadaster, BAG, CBS, WOZ, EP-Online,
                3DBAG, PDOK, DSO, RCE, and others. We process this data,
                run our own algorithms over it, and display the results.
              </p>
              <p>
                Critical things you need to understand:
              </p>
              <ul className="space-y-2 mt-3 ml-5 list-disc marker:text-brand-meadow">
                <li>
                  We do not own, verify, or guarantee the accuracy of any
                  underlying data.
                </li>
                <li>
                  Public registries may contain errors, omissions, or
                  out-of-date information.
                </li>
                <li>
                  Our scores and reports are algorithmic estimates, not
                  professional appraisals.
                </li>
                <li>
                  Property values, environmental risks, zoning
                  classifications, and other facts may change after we
                  display them.
                </li>
                <li>
                  Different sources sometimes disagree. We make our best
                  effort to reconcile discrepancies, but reconciliation is
                  not always possible.
                </li>
              </ul>
              <p className="mt-4 text-brand-linen">
                You must independently verify any fact that materially
                affects a decision you are about to make.
              </p>
            </LegalSection>

            <LegalSection num="1.6" title="Not professional advice">
              <p>
                PlotScore is an information service, not a professional
                service. Nothing on PlotScore is:
              </p>
              <ul className="space-y-2 mt-3 ml-5 list-disc marker:text-brand-meadow">
                <li>Financial advice or an investment recommendation</li>
                <li>Legal advice</li>
                <li>Tax advice</li>
                <li>A real estate appraisal in any regulated sense</li>
                <li>Engineering, structural, or building condition advice</li>
                <li>Environmental certification</li>
              </ul>
              <p className="mt-4">
                Property transactions are major financial decisions. Always
                consult qualified professionals &mdash; notaries, certified
                appraisers (taxateurs), legal counsel, surveyors, tax
                advisors &mdash; before acting on anything you see on
                PlotScore.
              </p>
            </LegalSection>

            <LegalSection num="1.7" title="Acceptable use">
              <p>
                You may use PlotScore for lawful purposes related to your
                own property research, evaluation, or due diligence. You
                may not:
              </p>
              <ul className="space-y-2 mt-3 ml-5 list-disc marker:text-brand-meadow">
                <li>
                  Resell, redistribute, or republish data from PlotScore as
                  a competing data product.
                </li>
                <li>
                  Scrape, mass-download, or attempt to bulk-extract our
                  content through automated means.
                </li>
                <li>
                  Reverse-engineer our scoring algorithms or attempt to
                  derive proprietary methodology.
                </li>
                <li>
                  Use PlotScore to harass, defame, or harm any person or
                  organization.
                </li>
                <li>
                  Circumvent password protection, rate limits, or any
                  access controls.
                </li>
                <li>
                  Use the service for any purpose that violates Dutch or
                  EU law.
                </li>
              </ul>
            </LegalSection>

            <LegalSection num="1.8" title="Intellectual property">
              <p>
                The PlotScore name, logo, scoring methodology, software,
                user interface, and the way we present and combine data
                are our intellectual property and protected by copyright,
                trademark, and other applicable laws. You may not copy,
                modify, distribute, or create derivative works without our
                prior written permission.
              </p>
              <p>
                The underlying public data (Kadaster, BAG, CBS, etc.)
                belongs to its respective sources and is governed by their
                terms. PlotScore does not claim ownership over that data.
              </p>
            </LegalSection>

            <LegalSection num="1.9" title="Termination">
              <p>
                We may suspend or terminate your access to PlotScore at
                any time, with or without notice, for any reason &mdash;
                including violation of these Terms. You may stop using
                PlotScore at any time. If you would like your account deleted, see Section 2.6 below.
              </p>
            </LegalSection>

            <LegalSection num="1.10" title="Limitation of liability">
              <p>
                To the maximum extent permitted by law:
              </p>
              <ul className="space-y-2 mt-3 ml-5 list-disc marker:text-brand-meadow">
                <li>
                  PlotScore is provided <em>as is</em> and{" "}
                  <em>as available</em>, without warranty of any kind,
                  express or implied.
                </li>
                <li>
                  We are not liable for any direct, indirect, incidental,
                  consequential, or special damages arising from your use
                  of PlotScore &mdash; including but not limited to lost
                  profits, lost data, missed opportunities, incorrect
                  property valuations, or decisions made in reliance on
                  our service.
                </li>
                <li>
                  Our total aggregate liability to you for any claim
                  arising from PlotScore is limited to{" "}
                  <span className="text-brand-linen">€100</span> or the
                  amount you have paid us in the past 12 months, whichever
                  is greater.
                </li>
              </ul>
              <p className="mt-4">
                These limitations apply even if we knew or should have
                known about the possibility of such damages. Some
                jurisdictions do not allow these exclusions, in which case
                they apply to the maximum extent permitted by applicable
                law.
              </p>
            </LegalSection>

            <LegalSection num="1.11" title="Indemnification">
              <p>
                You agree to defend, indemnify, and hold harmless PlotScore
                and its operators from any claim, damage, loss, or expense
                (including reasonable legal fees) arising from your use of
                PlotScore in violation of these Terms or applicable law.
              </p>
            </LegalSection>

            <LegalSection num="1.12" title="Governing law and jurisdiction">
              <p>
                These Terms are governed by the laws of the Netherlands.
                Any dispute arising from PlotScore that the parties cannot
                resolve amicably will be brought exclusively before the
                competent courts of Amsterdam, the Netherlands.
              </p>
            </LegalSection>

            <LegalSection num="1.13" title="Changes to these Terms">
              <p>
                We may update these Terms from time to time. If we make
                material changes, we will notify you by email (if we have
                your address) or by a clear notice on plotscore.app.
                Continued use of PlotScore after the effective date
                constitutes acceptance of the updated Terms. The
                &ldquo;Last updated&rdquo; date at the top of this page
                always reflects the current version.
              </p>
            </LegalSection>
          </section>

          {/* ═════════════════════════════════════════════
             PRIVACY POLICY
             ═════════════════════════════════════════════ */}
          <section id="privacy" className="mb-20 scroll-mt-24">
            <p className="text-[0.65rem] text-brand-meadow uppercase tracking-[0.25em] mb-3">
              Section 02
            </p>
            <h2 className="font-display text-[1.85rem] sm:text-[2.25rem] font-semibold text-brand-linen leading-[1.15] tracking-[0.01em] mb-10">
              Privacy Policy
            </h2>

            <LegalSection num="2.1" title="Who is responsible for your data">
              <p>
                The data controller for PlotScore is{" "}
                <span className="text-brand-linen">[Company Name B.V.]</span>,
                based at{" "}
                <span className="text-brand-linen">
                  [Registered address, Amsterdam, Netherlands]
                </span>
                . You can contact us about your personal data at{" "}
                <a
                  href="mailto:info@plotscore.app"
                  className="text-brand-meadow hover:underline"
                >
                  info@plotscore.app
                </a>
                .
              </p>
            </LegalSection>

            <LegalSection num="2.2" title="What data we collect">
              <p className="mb-4">
                <span className="text-brand-linen font-medium">
                  When you create an account
                </span>
                , we collect:
              </p>
              <ul className="space-y-2 ml-5 list-disc marker:text-brand-meadow mb-6">
                <li>Your email address</li>
                <li>Sign-in timestamps and authentication metadata</li>
              </ul>

              <p className="mb-4">
                <span className="text-brand-linen font-medium">
                  When you use the application
                </span>
                , we collect:
              </p>
              <ul className="space-y-2 ml-5 list-disc marker:text-brand-meadow mb-6">
                <li>
                  If you sign in with a Supabase-backed account: your
                  email address and authentication metadata
                </li>
                <li>Any properties you save (linked to your account)</li>
              </ul>

              <p className="mb-4">
                <span className="text-brand-linen font-medium">
                  When you browse plotscore.app
                </span>
                , our hosting provider automatically logs:
              </p>
              <ul className="space-y-2 ml-5 list-disc marker:text-brand-meadow mb-6">
                <li>Your IP address</li>
                <li>Browser user-agent string</li>
                <li>Pages visited and timestamps</li>
                <li>Referring URL (if any)</li>
              </ul>

              <p className="text-brand-linen">
                We do not run third-party analytics, advertising pixels,
                or any tracking beyond what is described above.
              </p>
            </LegalSection>

            <LegalSection num="2.3" title="Why we collect it (lawful basis)">
              <p>
                Under the General Data Protection Regulation (GDPR), we
                need a lawful basis for every type of processing. Here is
                ours:
              </p>
              <ul className="space-y-3 mt-3 ml-5 list-disc marker:text-brand-meadow">
                <li>
                  <span className="text-brand-linen">Account email</span>{" "}
                  &mdash; performance of a contract (Article 6(1)(b) GDPR). We need it to create and secure your account.
                </li>
                <li>
                  <span className="text-brand-linen">
                    App authentication and saved data
                  </span>{" "}
                  &mdash; performance of a contract (Article 6(1)(b) GDPR).
                  We need this to deliver the service you signed up for.
                </li>
                <li>
                  <span className="text-brand-linen">Server logs</span>{" "}
                  &mdash; legitimate interest (Article 6(1)(f) GDPR). We
                  need basic operational logs to run the service securely
                  and detect abuse. We have weighed this interest against
                  your privacy and consider it justified.
                </li>
              </ul>
            </LegalSection>

            <LegalSection num="2.4" title="Subprocessors">
              <p>
                We use a small number of trusted third-party services to
                run PlotScore. Each one is contractually bound to handle
                personal data in line with GDPR.
              </p>
              <ul className="space-y-3 mt-3 ml-5 list-disc marker:text-brand-meadow">
                <li>
                  <span className="text-brand-linen">Vercel</span> &mdash;
                  hosting and edge delivery (USA-based, EU regions
                  available)
                </li>
                <li>
                  <span className="text-brand-linen">Formspree</span>{" "}
                  &mdash; contact form submissions (USA-based)
                </li>
                <li>
                  <span className="text-brand-linen">Supabase</span>{" "}
                  &mdash; database and authentication for the application
                  (EU regions available)
                </li>
                <li>
                  <span className="text-brand-linen">Stripe</span>{" "}
                  &mdash; payment processing for paid plans (billing metadata only; we never receive full card numbers)
                </li>
                <li>
                  <span className="text-brand-linen">Resend</span>{" "}
                  &mdash; transactional and change-alert emails (EU regions available)
                </li>
                <li>
                  <span className="text-brand-linen">Cesium Ion / Google</span>{" "}
                  &mdash; 3D map tiles served inside the application (not
                  used on the landing page)
                </li>
              </ul>
              <p className="mt-4">
                When personal data leaves the EU/EEA, we rely on Standard
                Contractual Clauses (SCCs) approved by the European
                Commission, or equivalent transfer mechanisms.
              </p>
              <p className="mt-3 text-brand-linen">
                We do not sell your data. We do not share your data with
                advertisers. We do not share your data with anyone unless
                we are legally required to (for example, in response to a
                valid court order from a Dutch court).
              </p>
            </LegalSection>

            <LegalSection num="2.5" title="How long we keep your data">
              <ul className="space-y-3 ml-5 list-disc marker:text-brand-meadow">
                <li>
                  <span className="text-brand-linen">Account email</span>{" "}
                  &mdash; for as long as your account is open, or until you ask us to delete it.
                </li>
                <li>
                  <span className="text-brand-linen">App account data</span>{" "}
                  &mdash; for as long as your account exists. If you delete
                  your account, we delete it within 30 days.
                </li>
                <li>
                  <span className="text-brand-linen">Server logs</span>{" "}
                  &mdash; 30 days, then automatically deleted.
                </li>
                <li>
                  <span className="text-brand-linen">Saved properties</span>{" "}
                  &mdash; for the lifetime of your account. Deleted with
                  your account.
                </li>
              </ul>
            </LegalSection>

            <LegalSection num="2.6" title="Your rights under GDPR">
              <p>
                You have the following rights regarding your personal
                data. These are real legal rights, not policy nice-to-haves
                &mdash; we are required to honor them:
              </p>
              <ul className="space-y-3 mt-3 ml-5 list-disc marker:text-brand-meadow">
                <li>
                  <span className="text-brand-linen">Access</span> &mdash;
                  ask us what data we hold about you.
                </li>
                <li>
                  <span className="text-brand-linen">Rectification</span>{" "}
                  &mdash; correct any inaccurate data.
                </li>
                <li>
                  <span className="text-brand-linen">
                    Erasure (right to be forgotten)
                  </span>{" "}
                  &mdash; ask us to delete your data, subject to any legal
                  retention requirements.
                </li>
                <li>
                  <span className="text-brand-linen">Restriction</span>{" "}
                  &mdash; ask us to pause processing your data temporarily.
                </li>
                <li>
                  <span className="text-brand-linen">Portability</span>{" "}
                  &mdash; receive your data in a structured,
                  machine-readable format.
                </li>
                <li>
                  <span className="text-brand-linen">Object</span> &mdash;
                  object to processing based on legitimate interest.
                </li>
                <li>
                  <span className="text-brand-linen">Withdraw consent</span>{" "}
                  &mdash; if our processing is based on consent, you can
                  withdraw it at any time. Withdrawal does not affect the
                  lawfulness of processing carried out before the
                  withdrawal.
                </li>
              </ul>
              <p className="mt-4">
                To exercise any of these rights, email{" "}
                <a
                  href="mailto:info@plotscore.app"
                  className="text-brand-meadow hover:underline"
                >
                  info@plotscore.app
                </a>
                . We will respond within 30 days.
              </p>
            </LegalSection>

            <LegalSection num="2.7" title="Cookies">
              <p>We use strictly necessary cookies only:</p>
              <ul className="space-y-2 mt-3 ml-5 list-disc marker:text-brand-meadow">
                <li>
                  <span className="font-data text-brand-linen">app_access</span>{" "}
                  &mdash; a session cookie used to remember that you have
                  entered the access password for the protected
                  application route.
                </li>
                <li>
                  <span className="text-brand-linen">
                    Authentication cookies
                  </span>{" "}
                  &mdash; set by Supabase only if you sign in with an
                  account.
                </li>
              </ul>
              <p className="mt-4">
                We do not use marketing cookies, advertising cookies, or
                third-party tracking cookies. You do not see a cookie
                banner because we do not need consent for strictly
                necessary cookies under the ePrivacy Directive.
              </p>
            </LegalSection>

            <LegalSection num="2.8" title="Security">
              <p>
                We use industry-standard security measures including:
              </p>
              <ul className="space-y-2 mt-3 ml-5 list-disc marker:text-brand-meadow">
                <li>HTTPS everywhere (TLS 1.3)</li>
                <li>
                  Encrypted data at rest (provided by our hosting and
                  database subprocessors)
                </li>
                <li>Hashed passwords for any stored credentials</li>
                <li>Restricted access to production systems</li>
              </ul>
              <p className="mt-4">
                No system is completely secure. If we become aware of a
                personal data breach, we will notify you and the Autoriteit
                Persoonsgegevens (the Dutch Data Protection Authority)
                within 72 hours, as required by GDPR Article 33.
              </p>
            </LegalSection>

            <LegalSection num="2.9" title="Children">
              <p>
                PlotScore is not directed at children under 16. We do not
                knowingly collect personal data from children. If you
                believe we have inadvertently collected data from a child,
                please contact us at{" "}
                <a
                  href="mailto:info@plotscore.app"
                  className="text-brand-meadow hover:underline"
                >
                  info@plotscore.app
                </a>{" "}
                and we will delete it.
              </p>
            </LegalSection>

            <LegalSection num="2.10" title="Changes to this Policy">
              <p>
                We may update this Privacy Policy from time to time.
                Material changes will be communicated by email (if we have
                your address) and posted on this page with a new
                &ldquo;Last updated&rdquo; date. We encourage you to
                review this page periodically.
              </p>
            </LegalSection>
          </section>

          {/* ═════════════════════════════════════════════
             CONTACT
             ═════════════════════════════════════════════ */}
          <section id="contact" className="mb-16 scroll-mt-24">
            <p className="text-[0.65rem] text-brand-meadow uppercase tracking-[0.25em] mb-3">
              Section 03
            </p>
            <h2 className="font-display text-[1.85rem] sm:text-[2.25rem] font-semibold text-brand-linen leading-[1.15] tracking-[0.01em] mb-10">
              Contact &amp; Complaints
            </h2>

            <LegalSection num="3.1" title="Get in touch">
              <p>
                For any legal, privacy, or terms-related question, contact
                us:
              </p>
              <ul className="space-y-2 mt-3 ml-5 list-none">
                <li>
                  <span className="text-brand-muted text-[0.85rem] font-data uppercase tracking-[0.1em]">
                    Email
                  </span>
                  {" — "}
                  <a
                    href="mailto:info@plotscore.app"
                    className="text-brand-meadow hover:underline"
                  >
                    info@plotscore.app
                  </a>
                </li>
                <li>
                  <span className="text-brand-muted text-[0.85rem] font-data uppercase tracking-[0.1em]">
                    Privacy
                  </span>
                  {" — "}
                  <a
                    href="mailto:info@plotscore.app"
                    className="text-brand-meadow hover:underline"
                  >
                    info@plotscore.app
                  </a>
                </li>
                <li>
                  <span className="text-brand-muted text-[0.85rem] font-data uppercase tracking-[0.1em]">
                    Address
                  </span>
                  {" — "}
                  <span className="text-brand-linen">
                    [Registered address, Amsterdam, Netherlands]
                  </span>
                </li>
              </ul>
            </LegalSection>

            <LegalSection num="3.2" title="Filing a complaint">
              <p>
                If you believe we are handling your personal data
                improperly, you have the right to lodge a complaint with
                the Dutch Data Protection Authority:
              </p>
              <div className="mt-4 p-5 bg-brand-pine border border-brand-border rounded-xl">
                <p className="text-brand-linen font-medium mb-2">
                  Autoriteit Persoonsgegevens
                </p>
                <p className="text-[0.9rem] text-brand-sage leading-[1.7]">
                  Postbus 93374
                  <br />
                  2509 AJ Den Haag
                  <br />
                  Netherlands
                </p>
                <p className="text-[0.85rem] mt-3">
                  <a
                    href="https://autoriteitpersoonsgegevens.nl"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-meadow hover:underline"
                  >
                    autoriteitpersoonsgegevens.nl ↗
                  </a>
                </p>
              </div>
            </LegalSection>
          </section>

          {/* ── Footer note ── */}
          <div className="mt-20 pt-8 border-t border-brand-border">
            <p className="text-[0.7rem] text-brand-muted leading-[1.7] font-data">
              Last updated: {LAST_UPDATED}
            </p>
            <p className="text-[0.75rem] text-brand-muted leading-[1.7] mt-3">
              This document is governed by the laws of the Netherlands. By
              continuing to use PlotScore you confirm that you have read,
              understood, and agreed to both the Terms of Use and the
              Privacy Policy above.
            </p>
          </div>
        </article>
      </main>

      {/* ── Footer ── */}
      <footer className="py-8 border-t border-brand-border bg-brand-pine">
        <div className="max-w-[1120px] mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link
            href="/"
            className="font-display text-[1.1rem] font-semibold text-brand-muted tracking-[0.02em] hover:text-brand-linen transition-colors"
          >
            PlotScore
          </Link>
          <div className="flex items-center gap-6 text-[0.75rem] text-brand-muted">
            <span>Built in Amsterdam</span>
            <Link href="/legal#privacy" className="hover:text-brand-sage transition-colors">
              Privacy
            </Link>
            <Link href="/legal#terms" className="hover:text-brand-sage transition-colors">
              Terms
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ── Reusable section component ── */
function LegalSection({
  num,
  title,
  children,
}: {
  num: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-10">
      <div className="flex items-baseline gap-4 mb-4">
        <span className="font-data text-[0.7rem] text-brand-muted tabular-nums tracking-[0.1em] flex-shrink-0">
          {num}
        </span>
        <h3
          className="font-display text-[1.15rem] sm:text-[1.25rem] font-semibold text-brand-linen tracking-[0.01em]"
          dangerouslySetInnerHTML={{ __html: title }}
        />
      </div>
      <div className="ml-0 sm:ml-12 space-y-4 text-[0.95rem] text-brand-sage leading-[1.75]">
        {children}
      </div>
    </div>
  );
}
